import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Table, Text} from "@wfp/ui";
import {KVPair, ListPage, OrderItem, Sortable} from "../../models/types";
import {TablePagination} from "../commons/table-pagination/table-pagination";
import {StatusBadge} from "../commons/status-badge";

interface Props {
    hidePagination?: boolean;
    page: ListPage<OrderItem>;
    loadPage: (page: number, pageSize?: number, filtering?: KVPair[], sorting?: string[]) => void;
    onSelectOrderItems?: (orderItemsId: number[]) => void;
    sortInfo?: Sortable[];
    sort?: (field: string) => void;
}

const headers = ["commodities", "quantity", "state"];

const sort: Sortable[] = [
    {field: 'commodities', code: 'commodities', enabled: true, mode: 0},
    {field: 'quantity', code: 'quantity', enabled: true, mode: 0},
    {field: 'state', code: 'state', enabled: true, mode: 0},
];

export function OrderItemCommoditiesTable(props: Props) {
    const {t} = useTranslation();
    const [sortables, setSortables] = useState<Sortable[]>(sort);

    useEffect(() => {
        if (!!props.page.results && !!props.onSelectOrderItems) {
            const id = props.page.results.map((order) => order && order.id);
            props.onSelectOrderItems(id);
        }
    }, [props.page]);


    useEffect(() => {
        if (!!props.sortInfo) {
            const sorts = headers.map((header) => {
                const sort = props.sortInfo?.find((sort) => sort.code === header);
                return !!sort ? sort : {code: header, field: header, mode: 0, enabled: false}
            });
            setSortables(sorts);
        }
    }, [props.sortInfo]);

    // Extract the single order item this table displays
    const orderItem = props.page.results[0];

    // Early return if no order item exists
    if (!orderItem) {
        return <div>{t('no_data')}</div>;
    }

    function handleSorting(idx: number) {
        if (!!sortables && !!props.sort) {
            const sortable = sortables[idx];
            if (!sortable.enabled) {
                return;
            }
            sortable.mode = (sortable.mode + 1) % 3;
            setSortables([...sortables]);
            const value = sortable.mode === 1 ? `+${sortable.field}` : sortable.mode === 2 ? `-${sortable.field}` : sortable.field;
            props.sort(value);
        }
    }

    return (
        <>
            <div className='my-3'>
                <Text className='!mt-0' kind='p'>{t('school')}: <strong>{orderItem.schoolName}</strong></Text>
                <Text className='!mt-0'
                      kind='p'>{t('school_ref')}: <strong>{orderItem.schoolRef}</strong></Text>
            </div>
            <Table>
                <thead>
                <tr className="item-row-head">
                    {headers.map((header, idx) => (
                        <th key={idx} onClick={() => handleSorting(idx)}
                            className={props.sort && sortables?.at(idx)?.enabled ? "cursor-pointer" : ""}>
                            {t(header)}
                            {!!sortables?.at(idx) && (
                                <span className="ml-0.5">
                                {sortables[idx].mode === 2 ? '\u25bc' : sortables[idx].mode === 0 ? '\u2000' : '\u25b2'}
                            </span>
                            )}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {(orderItem.crates || []).map((item, idx) => (
                    <tr className="item-row" key={item.id}>
                        <td className="item-col text-start !align-middle">
                            {item.contentName}
                        </td>
                        <td className="item-col text-start !align-middle">
                            {item.quantity}
                        </td>
                        <td className="item-col text-start !align-middle">
                            <StatusBadge status={orderItem.pendingDiscardRequest ? "DISCARD_REQUESTED" : orderItem.state} />
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>
            {!props.hidePagination && (
                <TablePagination page={props.page} loadPage={props.loadPage}/>
            )}
        </>
    )
}
